import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import WelcomeStep from "./Steps/WelcomeStep";
import ExperienceStep from "./Steps/ExperienceStep";
import AboutYoutStep from "./Steps/AboutYouStep";
import PeopleStep from "./Steps/PeopleStep";
import SubscriptionStep from "./Steps/SubscriptionStep";
import DoneStep from "./Steps/DoneStep";
import PaymentMethodStep from "./Steps/PaymentMethodStep";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import PersonalInfoStep from "./Steps/PersonalInfoStep";
import BillingInfoStep from "./Steps/BillingInfoStep";
import { useLanguage } from "../Translations/LenguageContext";
import LanguageStep from "./Steps/LanguageStep";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;
const STRIPE_PUBLISHABLE_KEY_TEST = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY_TEST);

const MainOnboarding = () => {
    const { t } = useLanguage()
    const account_id = localStorage.getItem("account_id");
    const { onb_step } = useParams();
    const navigate = useNavigate();
    const [audioEnabled, setAudioEnabled] = useState(false);
    const [packages, setPackages] = useState([])
    const [loadingPackages, setLoadingPackages] = useState(false)

    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [error, setError] = useState({ message: "", display: false });
    const [loadingSteps, setLoadingSteps] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [subscription, setSelectedSubscription] = useState(null);
    const [selectedInterval, setSelectedInterval] = useState(null)
    const [selectedPackage, setSelectedPackage] = useState({})
    const [loadingSelectedPackage, setLoadingSelectedPackage] = useState({})

    const [infoAddress, setInfoAddress] = useState({})
    const [billingAddress, setBillingAddress] = useState({})
    const [billingSameDb, setBillingSameDb] = useState(false)
    const [loadingAddresses, setLoadingAddresses] = useState(false)

    const unlockAudio = () => {
        if (audioEnabled) return;

        const audio = new Audio("/sound/click_sound.mp3");
        audio.volume = 0;

        audio.play()
            .then(() => setAudioEnabled(true))
            .catch(() => { });
    };

    const fetchUserData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ account_id }),
            });

            if (!response.ok) throw new Error("Failed to fetch user session");

            const data = await response.json();
            setUserData(data);

            setTimeout(() => setLoading(false), 2000);
        } catch (error) {
            setLoading(false)
            setError({ message: "Error fetching session... redirecting to login", display: true });
            setTimeout(() => {
                console.error("Error fetching session:", error);
                navigate("/login");
            }, 2000);
        }
    };

    const fetchPackages = async () => {
        setError({ message: "", display: false });
        setLoadingPackages(true);
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_packages`, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            });

            if (!response.ok) throw new Error("Failed to fetch user session");

            const data = await response.json();
            setPackages(data.data);

            setTimeout(() => setLoadingPackages(false), 2000);
        } catch (error) {
            console.error("Error fetching session:", error);
            setLoadingPackages(false)
            setError({ message: "An error occurred while getting packages... retry in a few minutes", display: true })
        }
    };

    const fecthSteps = async () => {
        setError({ message: "", display: false });
        setLoading(true);
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_steps`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ account_id }),
            });
            if (!response.ok) throw new Error("Failed to fetch user session");

            const data = await response.json();
            const result = data.data
            const completed = data.completed

            if (completed && onb_step != "done") {
                navigate("/e8console/organizations")
            }

            if (result && Object.keys(result).length > 0) {
                const selected_options_db = result.selected_options
                const selected_people_db = result.selected_people
                const interval_db = result.subscription.interval
                const plan_db = result.subscription.plan

                setSelectedOptions(selected_options_db)
                setSelectedTeam(selected_people_db)
                setSelectedInterval(interval_db)
                setSelectedSubscription(plan_db)
            }

            setTimeout(() => setLoading(false), 2000);
        } catch (error) {
            console.error("Error fetching session:", error);
            setLoading(false)
            setError({ message: "An error occurred while getting data... retry in a few minutes", display: true })
        }
    };

    const fetchPaymentPlan = async () => {
        setError({ message: "", display: false });
        setLoadingSelectedPackage(true);
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_plan`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ account_id }),
            });

            if (!response.ok) throw new Error("Failed to fetch user session");

            const data = await response.json();
            const result = data.data
            if (result) {
                setSelectedPackage(result)
            }

            setTimeout(() => setLoadingSelectedPackage(false), 2000);
        } catch (error) {
            console.error("Error fetching session:", error);
            setLoadingSelectedPackage(false)
            setError({ message: "An error occurred while getting the plan... retry in a few minutes", display: true })
        }
    };

    const fetchAddress = async () => {
        setError({ message: "", display: false });
        setLoadingAddresses(true);
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_addresses`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ account_id }),
            });

            if (!response.ok) throw new Error("Failed to fetch user session");

            const data = await response.json();
            const status = data.status
            if (status == "OK") {
                setInfoAddress(data.info_address)
                setBillingAddress(data.billing_address)
                setBillingSameDb(data.billing_same)
            }

            setTimeout(() => setLoadingAddresses(false), 2000);
        } catch (error) {
            console.error("Error fetching addresses:", error);
            setLoadingAddresses(false)
            setError({ message: "An error occurred while getting the plan... retry in a few minutes", display: true })
        }
    };

    const handleStep = async (type, step) => {
        soundClick();
        setError({ message: "", display: false });

        if (type === "back") {
            navigate(`/onboarding/${step}`);
            return;
        }

        if (type === "continue") {
            setLoadingSteps(true);

            if (selectedOptions.length < 3 && onb_step === "about-you") {
                setError({ message: t.onboarding.five_options, display: true });
                setLoadingSteps(false);
                return;
            }

            if (!selectedTeam && onb_step === "people") {
                setError({ message: t.onboarding.one_option, display: true });
                setLoadingSteps(false);
                return;
            }

            try {
                const response = await fetch(`${GEN_BACKEND_LINK}/save_steps`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        account_id,
                        step: onb_step,
                        selected_options: selectedOptions,
                        selected_people: selectedTeam,
                        subscription: {
                            interval: selectedInterval,
                            plan: subscription
                        }
                    }),
                });

                if (!response.ok) throw new Error("Failed");

                setTimeout(() => {
                    navigate(`/onboarding/${step}`);
                    setLoadingSteps(false);
                }, 2000);

            } catch (error) {
                console.error(error);
                setLoadingSteps(false);
                setError({ message: "An error occurred, try again later", display: true });
            }
        }
    };

    const soundClick = () => {
        if (!audioEnabled) return;

        const audio = new Audio("/sound/click_sound.mp3");
        audio.play().catch(() => { });
    };

    useEffect(() => {
        unlockAudio()
        if (onb_step === "welcome") {
            fetchUserData();
        }
        if (onb_step == "subscription") {
            fetchPackages()
        }
        if (onb_step == "info" || onb_step == "billing-info") {
            fetchAddress()
        }
        if (onb_step == "payment-method") {
            fetchPaymentPlan()
        } else {
            fecthSteps()
        }
    }, [onb_step]);

    useEffect(() => {
        unlockAudio()
        if (onb_step === "done" && !loading) {
            const audio = new Audio("/sound/ready_sound.mp3");
            audio.play().catch(() => { });
        }
    }, [onb_step, loading]);

    const renderStep = () => {
        switch (onb_step) {
            case "welcome":
                return (
                    <WelcomeStep
                        userData={userData}
                        loading={loading}
                        handleStep={handleStep}
                        error={error}
                        loadingSteps={loadingSteps}
                    />
                );

            case "language":
                return (
                    <LanguageStep
                        handleStep={handleStep}
                        soundClick={soundClick}
                        error={error}
                        loadingSteps={loadingSteps}
                    />
                );

            case "experience":
                return (
                    <ExperienceStep
                        handleStep={handleStep}
                        soundClick={soundClick}
                        error={error}
                        loadingSteps={loadingSteps}
                    />
                );

            case "info":
                return (
                    <PersonalInfoStep
                        account_id={account_id}
                        handleStep={handleStep}
                        error={error}
                        setError={setError}
                        loadingAddresses={loadingAddresses}
                        infoAddress={infoAddress}
                        billingSameDb={billingSameDb}
                    />
                );

            case "billing-info":
                return (
                    <BillingInfoStep
                        account_id={account_id}
                        handleStep={handleStep}
                        error={error}
                        setError={setError}
                        loadingAddresses={loadingAddresses}
                        billingAddress={billingAddress}
                    />
                );

            case "about-you":
                return (
                    <AboutYoutStep
                        handleStep={handleStep}
                        soundClick={soundClick}
                        error={error}
                        loadingSteps={loadingSteps}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                    />
                );

            case "people":
                return (
                    <PeopleStep
                        handleStep={handleStep}
                        soundClick={soundClick}
                        error={error}
                        loadingSteps={loadingSteps}
                        selectedTeam={selectedTeam}
                        setSelectedTeam={setSelectedTeam}
                    />
                );

            case "subscription":
                return (
                    <SubscriptionStep
                        handleStep={handleStep}
                        soundClick={soundClick}
                        error={error}
                        loadingSteps={loadingSteps}
                        packages={packages}
                        loadingPackages={loadingPackages}
                        subscription={subscription}
                        setSelectedSubscription={setSelectedSubscription}
                        selectedInterval={selectedInterval}
                        setSelectedInterval={setSelectedInterval}
                    />
                );

            case "payment-method":
                return (
                    <Elements stripe={stripePromise}>
                        <PaymentMethodStep
                            account_id={account_id}
                            handleStep={handleStep}
                            soundClick={soundClick}
                            error={error}
                            subscription={subscription}
                            selectedPackage={selectedPackage}
                            loadingSelectedPackage={loadingSelectedPackage}
                            setError={setError}
                        />
                    </Elements>
                );

            case "done":
                return <DoneStep soundClick={soundClick} />;

            default:
                return "This page does not exist"
        }
    };

    return (
        <div className="onboarding_container">
            {renderStep()}
        </div>
    );
};

export default MainOnboarding;